import PropTypes from 'prop-types';
import { Box, Skeleton } from '@mui/material';
import FieldRow from 'features/IRWorkstation/components/Common/FieldRow';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import { formatCurrency } from 'utils/utils';

const PLACEHOLDER = '-';

const valueOrPlaceholder = (value) =>
	value === null || value === undefined || value === '' ? PLACEHOLDER : value;

const formatAddress = (address) =>
	[
		address?.street,
		address?.city,
		address?.state,
		address?.country,
		address?.postal_code,
	]
		.filter(Boolean)
		.join(', ') || PLACEHOLDER;

const formatAssetsUnderManagement = (value) =>
	value === null || value === undefined
		? PLACEHOLDER
		: formatCurrency(value, 'USD', { compact: true, decimalPlaces: 1 });

const PROFILE_FIELD_LABELS = [
	'Internal Coverage',
	'Investor Type',
	'Assets Under Management',
	'Check Size',
	'Billing Address',
	'Source',
];

const LPProfileSkeleton = () => (
	<Box
		data-testid='lp-profile-skeleton'
		sx={{
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
		}}
	>
		{PROFILE_FIELD_LABELS.map((label) => (
			<Box
				key={label}
				data-testid='lp-profile-skeleton-row'
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					py: '3px',
				}}
			>
				<Skeleton width='35%' height={18} />
				<Skeleton width='30%' height={18} />
			</Box>
		))}
	</Box>
);

const LPProfileCard = ({ account, loading = false }) => (
	<Box
		data-testid='lp-profile-card'
		sx={{ width: '100%', display: 'flex', '& > *': { flex: 1 } }}
	>
		<QuadPanel
			title='LP profile'
			badge={valueOrPlaceholder(account?.coverage_tier)}
			badgeColor='blue'
			badgeLoading={loading}
		>
			<Box
				sx={{
					height: '100%',
					boxSizing: 'border-box',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					padding: '9px 14px',
				}}
			>
				{loading ? (
					<LPProfileSkeleton />
				) : (
					<>
						<FieldRow
							label='Internal Coverage'
							value={valueOrPlaceholder(account?.internal_coverage)}
						/>
						<FieldRow
							label='Investor Type'
							value={valueOrPlaceholder(account?.investor_type)}
						/>
						<FieldRow
							label='Assets Under Management'
							value={formatAssetsUnderManagement(
								account?.assets_under_management
							)}
						/>
						<FieldRow
							label='Check Size'
							value={valueOrPlaceholder(account?.check_size)}
						/>
						<FieldRow
							label='Billing Address'
							value={formatAddress(account?.billing_address)}
						/>
						<FieldRow
							label='Source'
							value={valueOrPlaceholder(account?.source)}
						/>
					</>
				)}
			</Box>
		</QuadPanel>
	</Box>
);

LPProfileCard.propTypes = {
	account: PropTypes.shape({
		coverage_tier: PropTypes.string,
		internal_coverage: PropTypes.string,
		investor_type: PropTypes.string,
		assets_under_management: PropTypes.number,
		check_size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		billing_address: PropTypes.shape({
			street: PropTypes.string,
			city: PropTypes.string,
			state: PropTypes.string,
			country: PropTypes.string,
			postal_code: PropTypes.string,
		}),
		source: PropTypes.string,
	}),
	loading: PropTypes.bool,
};

export default LPProfileCard;
